import { format } from 'date-fns';

import { DateFormat } from 'core/global.enum';
import { AppMessages } from 'i18n/messages';
import { DodQueueSummaryV2, DodTimeseriesSeries } from 'api/dod/dod.v2.types';

/**
 * Formatting and series maths shared by the Przeglad and Kolejki tabs.
 *
 * The single rule every helper here obeys: unknown is not zero. A null KPI, an absent rollup
 * provider and an empty series each render as "unknown" or as nothing at all — never as a zero
 * that reads like "the system is quiet".
 */

/** What the UI shows when a value is genuinely unknown. Callers pass the localised string. */
export const formatKpiValue = (value: number | null, unknownLabel: string): string =>
  value === null || value === undefined ? unknownLabel : formatCount(value);

/**
 * Grouped integer in the browser's own locale.
 *
 * Deliberately not pinned to 'pl-PL': the app ships EN and PL, and a Polish thousands separator
 * on an English screen is exactly the kind of detail an operator reads as a bug.
 */
export const formatCount = (value: number): string => new Intl.NumberFormat(undefined).format(Math.round(value));

/** One decimal, for a rate like "48.3 / min". Whole numbers stay whole. */
export const formatRate = (value: number | null, unknownLabel: string): string => {
  if (value === null || value === undefined) {
    return unknownLabel;
  }

  return Number.isInteger(value) ? formatCount(value) : value.toFixed(1);
};

/** "2m 14s", "1h 03m", "3d 4h". Null renders as the unknown label. */
export const formatDurationSeconds = (seconds: number | null, unknownLabel: string): string => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return unknownLabel;
  }

  const total = Math.max(0, Math.round(seconds));

  if (total < 60) {
    return `${total}s`;
  }

  if (total < 3600) {
    return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
  }

  if (total < 86400) {
    return `${Math.floor(total / 3600)}h ${String(Math.floor((total % 3600) / 60)).padStart(2, '0')}m`;
  }

  return `${Math.floor(total / 86400)}d ${Math.floor((total % 86400) / 3600)}h`;
};

/**
 * Relative time in the past, e.g. "42 s temu" built by the caller from the returned parts.
 * Returns null when the timestamp is absent, so the caller renders a dash rather than "NaN".
 */
export const relativeSecondsSince = (iso?: string | null): number | null => {
  if (!iso) {
    return null;
  }

  const parsed = Date.parse(iso);

  return Number.isNaN(parsed) ? null : Math.max(0, Math.round((Date.now() - parsed) / 1000));
};

/** Epoch milliseconds as the app's full date format, so rows from different days can be told apart. */
export const formatTimestamp = (timestamp: number): string => format(new Date(timestamp), DateFormat.FULL);

export const formatDateTime = (iso?: string | null): string | null => {
  if (!iso) {
    return null;
  }

  const parsed = Date.parse(iso);

  return Number.isNaN(parsed) ? null : new Date(parsed).toLocaleString();
};

/**
 * Turn counter buckets into a per-minute rate series.
 *
 * The rollups store the increment that happened inside each bucket (HINCRBY), so a rate is the
 * bucket value scaled by how many buckets fit in a minute. `step` is the step the backend
 * actually served, in milliseconds — never the one that was asked for.
 */
export const toPerMinute = (series: DodTimeseriesSeries, stepMs: number): Array<[number, number | null]> => {
  const scale = stepMs > 0 ? 60_000 / stepMs : 1;

  return series.points.map(([at, value]) => [at, value === null ? null : value * scale]);
};

/** Sum several series point-wise onto the first one's timestamps. Used for "all TKDs" totals. */
export const sumSeries = (series: DodTimeseriesSeries[]): Array<[number, number | null]> => {
  if (series.length === 0) {
    return [];
  }

  return series[0].points.map(([at], index) => {
    const values = series
      .map((one) => one.points[index]?.[1])
      .filter((value): value is number => value !== null && value !== undefined);

    return [at, values.length === 0 ? null : values.reduce((total, value) => total + value, 0)];
  });
};

/** The `n` series with the highest last known value. For "top 5 TKD". */
export const topSeriesByLastValue = (series: DodTimeseriesSeries[], count: number): DodTimeseriesSeries[] => {
  const lastValue = (one: DodTimeseriesSeries): number => {
    for (let index = one.points.length - 1; index >= 0; index -= 1) {
      const value = one.points[index][1];

      if (value !== null && value !== undefined) {
        return value;
      }
    }

    return 0;
  };

  return [...series].sort((left, right) => lastValue(right) - lastValue(left)).slice(0, count);
};

/** Readable label for a grouped series: the TKD name when the labels carry one, else its key. */
export const seriesLabel = (series: DodTimeseriesSeries): string => {
  const { rcpId, nlk, command } = series.labels;

  if (nlk) {
    return `NLK ${nlk}`;
  }

  if (command) {
    return command;
  }

  return rcpId ?? series.key;
};

/** Group queue rows by RCP server, preserving the order the API returned them in. */
export const groupQueuesByRcp = (
  rows: DodQueueSummaryV2[],
): Array<{ rcpId: string; rcpNazwa?: string; rows: DodQueueSummaryV2[] }> => {
  const groups = new Map<string, { rcpId: string; rcpNazwa?: string; rows: DodQueueSummaryV2[] }>();

  rows.forEach((row) => {
    const existing = groups.get(row.rcpId);

    if (existing) {
      existing.rows.push(row);

      return;
    }

    groups.set(row.rcpId, { rcpId: row.rcpId, rcpNazwa: row.rcpNazwa, rows: [row] });
  });

  return Array.from(groups.values());
};

export type DodQueueTotals = {
  queues: number;
  depth: number;
  dlq: number;
};

export const sumQueueTotals = (rows: DodQueueSummaryV2[]): DodQueueTotals =>
  rows.reduce<DodQueueTotals>(
    (totals, row) => ({
      queues: totals.queues + 1,
      depth: totals.depth + row.depth,
      dlq: totals.dlq + row.dlqDepth,
    }),
    { queues: 0, depth: 0, dlq: 0 },
  );

/** Truncate an error to one table cell's worth; the full text goes in a Tooltip. */
export const truncateError = (message: string | undefined, max = 48): string | null => {
  if (!message) {
    return null;
  }

  return message.length > max ? `${message.slice(0, max - 1)}…` : message;
};

/**
 * The one way a reader is named across the monitor. Names repeat across controllers (STG: NLK 60 and
 * 64 are both "Nazwa kanału nr 1"), so the NLK is always part of it.
 */
export const dodReaderLabel = (tkdNazwa: string | null | undefined, nlk: number | string): string =>
  tkdNazwa ? `${tkdNazwa} · NLK ${nlk}` : `NLK ${nlk}`;

/** Distinct RCP servers in a queue list, as toolbar select options. */
export const toRcpOptions = (rows: DodQueueSummaryV2[]): Array<{ label: string; value: string }> => {
  const byId = new Map<string, string>();

  rows.forEach((row) => byId.has(row.rcpId) || byId.set(row.rcpId, row.rcpNazwa ?? row.rcpId));

  return Array.from(byId.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

/** TKDs in a queue list, already narrowed by whatever RCP filter produced `rows`. */
export const toTkdOptions = (rows: DodQueueSummaryV2[]): Array<{ label: string; value: string }> => {
  const byNlk = new Map<string, string>();

  rows.forEach((row) => byNlk.set(String(row.nlk), dodReaderLabel(row.tkdNazwa, row.nlk)));

  return Array.from(byNlk.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => Number(left.value) - Number(right.value));
};

/**
 * The backend records a few machine reasons verbatim in `lastError`: the operator's pause, and the
 * two drain preconditions (DrainNotAttemptedReason). They are stable identifiers, not prose, so the
 * UI translates them; anything else (a controller status, a transport error) is shown as recorded.
 */
export const DOD_REASON_MESSAGE_IDS: Record<string, string> = {
  'paused by operator': 'dod.reason.pausedByOperator',
  'rcp-not-connected': 'dod.reason.rcpNotConnected',
  'tkd-not-ok': 'dod.reason.tkdNotOk',
};

export const dodReasonLabel = (
  reason: string | null | undefined,
  formatMessage: (descriptor: { id: string }) => string,
): string | null => {
  if (!reason) {
    return null;
  }
  const id = DOD_REASON_MESSAGE_IDS[reason.trim()];

  return id ? formatMessage({ id }) : reason;
};

const OUTBOX_STATUS_KEYS = [
  'PENDING',
  'CLAIMED',
  'DISPATCHED',
  'DELIVERED',
  'SUPERSEDED',
  'DEAD_LETTER',
  'RECONCILIATION_REQUIRED',
] as const;

/** The outbox status as the operator's word; an unknown wire value is shown as it came. */
export const dodOutboxStatusLabel = (status: string, formatMessage: (descriptor: { id: string }) => string): string =>
  (OUTBOX_STATUS_KEYS as readonly string[]).includes(status)
    ? formatMessage({ id: AppMessages[`dod.outbox.status.${status}` as keyof typeof AppMessages] })
    : status;
